/** Separator between layer name and field label for Other-layer GIS display (em dash). */
export const OTHER_LAYER_FIELD_NAME_SEPARATOR = ' — '

export const OTHER_LAYER_NAME_FIELD_LABEL = 'name'

/** Builds the prefixed display name for an Other-layer field row. */
export const formatOtherLayerFieldName = (layerName: string, fieldLabel: string): string =>
  `${layerName}${OTHER_LAYER_FIELD_NAME_SEPARATOR}${fieldLabel}`

export type OtherLayerFieldSortInput = {
  fieldLabel: string
  stringifiedValue?: string | null
}

/**
 * Sort priority for Other-layer fields — mirrors Property Tab ordering:
 * entity name value first, then fields labeled "name", then everything else by label.
 */
export const getOtherLayerFieldSortPriority = (
  field: OtherLayerFieldSortInput,
  entityName?: string | null
): number => {
  const normalizedEntityName = entityName?.trim().toLowerCase()
  const fieldValue = field.stringifiedValue?.trim().toLowerCase()

  if (normalizedEntityName && fieldValue === normalizedEntityName) {
    return 0
  }

  if (field.fieldLabel.trim().toLowerCase() === OTHER_LAYER_NAME_FIELD_LABEL) {
    return 1
  }

  return 2
}

/** Stable sort for Other-layer entity version fields (name-like fields before alphabetical label). */
export const sortOtherLayerFieldsForDisplay = <T extends OtherLayerFieldSortInput>(
  fields: T[],
  entityName?: string | null
): T[] =>
  [...fields].sort((fieldA, fieldB) => {
    const priorityDiff =
      getOtherLayerFieldSortPriority(fieldA, entityName) -
      getOtherLayerFieldSortPriority(fieldB, entityName)
    if (priorityDiff !== 0) {
      return priorityDiff
    }

    return fieldA.fieldLabel.localeCompare(fieldB.fieldLabel)
  })
